import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import '../../../index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Button from 'react-bootstrap/Button';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ConfirmacaoAgendamento from '../../../components/ConfirmacaoAgendamento';
import { useColors } from '../../../contexts/ColorContext';
import {
  getServicos,
  getHorariosDisponiveis,
  getClienteByPhone,
  criarCliente,
  criarAgendamento,
  getClienteMasterBySlug,
} from "../../../services/endpoints";
import { API_BASE_URL } from "../../../config";

// Helpers
const getIdServico = (s) => s?.idServico ?? s?.id;
const getIdFuncionario = (f) => f?.idFuncionario ?? f?.id;

const formatDateToDisplay = (isoDate) => {
  if (!isoDate) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate;
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

const normalizeServicos = (data) =>
  (data || []).map((s) => {
    const funcionariosDiretos = Array.isArray(s.funcionarios) ? s.funcionarios : null;
    const funcionariosViaJoin = Array.isArray(s.funcionariosServicos)
      ? s.funcionariosServicos
          .map((fs) => fs.funcionario || fs)
          .filter(Boolean)
          .map((f) => ({
            idFuncionario: getIdFuncionario(f),
            nome: f.nome,
            imagemUrl: f.imagemUrl ?? null,
          }))
      : [];

    const funcionarios = funcionariosDiretos
      ? funcionariosDiretos.map((f) => ({
          idFuncionario: getIdFuncionario(f),
          nome: f.nome,
          imagemUrl: f.imagemUrl ?? null,
        }))
      : funcionariosViaJoin;

    return {
      idServico: getIdServico(s),
      id: getIdServico(s),
      nome: s.nome,
      preco: s.preco,
      duracaoMinutos: s.duracaoMinutos,
      ativo: s.ativo,
      imagemUrl: s.imagemUrl ?? null,
      funcionarios,
    };
  });

function Agendamentos() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();

  // ── Usa clienteMaster passado via navigate state (sem re-fetch) ──────────
  const [clienteMaster, setClienteMaster] = useState(location.state?.clienteMaster ?? null);
  const [idClienteMaster, setIdClienteMaster] = useState(location.state?.clienteMaster?.id ?? null);

  const [selectedService, setSelectedService] = useState(null);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
 const today = new Date();

const todayISO = [
  today.getFullYear(),
  String(today.getMonth() + 1).padStart(2, "0"),
  String(today.getDate()).padStart(2, "0")
].join("-");
  const [selectedTime, setSelectedTime] = useState(null);

  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);
  const [clienteId, setClienteId] = useState(null);

  const [servicos, setServicos] = useState([]);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingServicos, setLoadingServicos] = useState(true);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [errorServicos, setErrorServicos] = useState(null);
  const [errorHorarios, setErrorHorarios] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const [showConfirm, setShowConfirm] = useState(false);
  const openConfirm = () => setShowConfirm(true);
  const closeConfirm = () => setShowConfirm(false);

  const { updateColors } = useColors();

  // Se veio direto pela URL (sem state), busca clienteMaster uma única vez
  useEffect(() => {
    if (clienteMaster) {
      if (clienteMaster.configuracaoCores) updateColors(clienteMaster.configuracaoCores);
      return;
    }
    if (!slug) return;

    getClienteMasterBySlug(slug)
      .then((data) => {
        setClienteMaster(data);
        setIdClienteMaster(data.id);
        if (data?.configuracaoCores) updateColors(data.configuracaoCores);
      })
      .catch((err) => console.error("Erro ao buscar ClienteMaster:", err));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Carregar serviços assim que tiver idClienteMaster
  useEffect(() => {
    if (!idClienteMaster) return;

    setLoadingServicos(true);
    setErrorServicos(null);

    getServicos(idClienteMaster)
      .then((data) => {
        const sorted = normalizeServicos(data).sort((a, b) => b.preco - a.preco);
        setServicos(sorted);
      })
      .catch((err) => setErrorServicos(err?.response?.data?.message || err.message))
      .finally(() => setLoadingServicos(false));
  }, [idClienteMaster]);

  // Buscar horários disponíveis
  useEffect(() => {
    setAvailableTimes([]);
    setSelectedTime(null);

    const idServico = getIdServico(selectedService);
    const idFuncionario = getIdFuncionario(selectedFuncionario);

    if (!idServico || !idFuncionario || !selectedDate || !idClienteMaster) return;

    setLoadingHorarios(true);
    setErrorHorarios(null);

    getHorariosDisponiveis({
      idClienteMaster,
      idFuncionario,
      idServico,
      from: selectedDate,
      to: selectedDate,
    })
      .then((data) => {
        // ── CORREÇÃO: API retorna { Data, Hora } com PascalCase ──────────────
        const times = (data || []).map((x, idx) => ({
          id: x.id ?? x.idHorario ?? idx,
          hora: x.Hora ?? x.hora ?? x.time,   // aceita ambos os casos
        }));

        const isToday = selectedDate === todayISO;
        let filtered = times;

        if (isToday) {
          const currentHM = `${String(today.getHours()).padStart(2,'0')}:${String(today.getMinutes()).padStart(2,'0')}`;
          filtered = times.filter(t => t.hora >= currentHM);
        }

        setAvailableTimes(filtered);
      })
      .catch((err) => setErrorHorarios(err?.response?.data?.message || err.message))
      .finally(() => setLoadingHorarios(false));
  }, [selectedFuncionario, selectedDate, selectedService, idClienteMaster]);

  // Avança automaticamente para passo 3 quando tudo estiver escolhido
  useEffect(() => {
    if (selectedService && selectedFuncionario && selectedDate && selectedTime) {
      setCurrentStep(3);
    }
  }, [selectedService, selectedFuncionario, selectedDate, selectedTime]);

  const handleSelectService = (service) => {
    setSelectedService(service);
    const linked = service.funcionarios || [];
    if (linked.length === 1) {
      setSelectedFuncionario({
        idFuncionario: getIdFuncionario(linked[0]),
        nome: linked[0].nome,
        imagemUrl: linked[0].imagemUrl,
      });
      setCurrentStep(2);
    } else {
      setSelectedFuncionario(null);
      setCurrentStep(1);
    }
  };

  const handleSelectFuncionario = (funcionario) => {
    setSelectedFuncionario({
      idFuncionario: getIdFuncionario(funcionario),
      nome: funcionario.nome,
      imagemUrl: funcionario.imagemUrl,
    });
    setCurrentStep(2);
  };

  const handleBack = () => {
    if (currentStep === 3) {
      setSelectedTime(null);
      setCurrentStep(2);
    } else {
      setCurrentStep((prev) => Math.max(prev - 1, 0));
    }
  };

  const handlePhoneChange = async (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 11) val = val.slice(0, 11);

    const masked = val
      .replace(/^(\d{2})(\d{5})(\d{0,4})$/, "($1)$2-$3")
      .replace(/-$/, "");
    setPhone(masked);

    if (val.length < 11) {
      setIsRegistered(false);
      setClienteId(null);
      setName("");
      return;
    }

    if (val.length === 11) {
      const numeroComDDI = "55" + val;
      try {
        const cliente = await getClienteByPhone(numeroComDDI, idClienteMaster);
        if (cliente?.id) {
          setIsRegistered(true);
          setClienteId(cliente.id);
          setName(cliente.nome || "");
        } else {
          setIsRegistered(false);
          setClienteId(null);
          setName("");
        }
      } catch {
        setIsRegistered(false);
        setClienteId(null);
        setName("");
      }
    }
  };

  const handleConfirmAgendamento = async () => {
    try {
      closeConfirm();
      if (!selectedService || !selectedFuncionario || !selectedDate || !selectedTime) {
        window.globalAlert("Selecione serviço, profissional, data e horário.", "info");
        return;
      }

      let resolvedClienteId = clienteId;
      const digits = phone.replace(/\D/g, "");

      if (!resolvedClienteId) {
        const novoCliente = await criarCliente({
          nome: name?.trim() || "Cliente",
          telefone: digits,
          email: "",
          clienteMasterId: idClienteMaster,
        });
        resolvedClienteId = novoCliente?.id ?? novoCliente?.Id;
        if (!resolvedClienteId) {
          window.globalAlert("Não foi possível criar o cliente.", "error");
          return;
        }
      }

      const dataIso = `${selectedDate}T${selectedTime}:00`;

      await criarAgendamento({
        ClienteMasterId: idClienteMaster,
        ClienteId: resolvedClienteId,
        ServicoId: getIdServico(selectedService),
        FuncionarioId: getIdFuncionario(selectedFuncionario),
        DataHora: dataIso,
        Observacao: notes || null,
      });

      window.globalAlert("Agendamento criado com sucesso!", "success");

      setPhone(""); setName(""); setNotes("");
      setSelectedService(null); setSelectedFuncionario(null);
      setSelectedDate(null); setSelectedTime(null);
      setIsRegistered(false); setClienteId(null);
      setCurrentStep(0);
    } catch (err) {
      if (err.response?.status === 409) {
        window.globalAlert("Esse horário já está reservado. Escolha outro.", "info");
        setShowConfirm(false);
        setCurrentStep(2);
        setSelectedTime(null);
      } else {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          "Erro ao agendar.";
        window.globalAlert(msg, "error");
      }
      closeConfirm();
    }
  };

  const steps = ["Serviço", "Profissional", "Data e Hora", "Seus Dados"];

  return (
    <>
      <div className="container">
        {/* Passos */}
        <div className="steps">
          {steps.map((step, index) => {
            let statusClass = "";
            if (index < currentStep) statusClass = "completed-current";
            else if (index === currentStep) statusClass = "active";
            return (
              <div key={index} className="step">
                <div className={`circle ${statusClass}`}>{index + 1}</div>
                <span className="step-label">{step}</span>
              </div>
            );
          })}
        </div>

        <div className="tab-content">
          {/* Passo 0: Serviços */}
          {currentStep === 0 && (
            <>
              <h1 className="titulo">Escolha o Serviço Desejado</h1>
              <div className="cards">
                {loadingServicos ? (
                  <div>Carregando serviços...</div>
                ) : errorServicos ? (
                  <div className="text-danger">Erro ao carregar serviços: {errorServicos}</div>
                ) : (
                  servicos.map((service) => (
                    <div
                      key={getIdServico(service)}
                      className={`card ${getIdServico(selectedService) === getIdServico(service) ? "selected" : ""}`}
                      onClick={() => handleSelectService(service)}
                    >
                      <div className="card-content">
                        {service.id && (
                          <img
                            src={`${API_BASE_URL}/Servicos/${service.id}/image`}
                            alt={service.nome}
                            className="card-image"
                          />
                        )}
                        <h2>{service.nome}</h2>
                        <p>R$ {Number(service.preco).toFixed(2)} • {service.duracaoMinutos} min</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Passo 1: Profissionais */}
          {currentStep === 1 && (
            <>
              <h1 className="titulo">Escolha o Profissional</h1>
              <div className="cards">
                {(selectedService?.funcionarios || []).map((funcionario) => (
                  <div
                    key={getIdFuncionario(funcionario)}
                    className={`card ${getIdFuncionario(selectedFuncionario) === getIdFuncionario(funcionario) ? "selected" : ""}`}
                    onClick={() => handleSelectFuncionario(funcionario)}
                  >
                    <div className="card-content">
                      {funcionario.idFuncionario && (
                        <img
                          src={`${API_BASE_URL}/Funcionarios/${funcionario.idFuncionario}/image`}
                          alt={funcionario.nome}
                          className="card-image"
                        />
                      )}
                      <h2>{funcionario.nome}</h2>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="secondary" onClick={handleBack} className="mt-3">Voltar</Button>
            </>
          )}

          {/* Passo 2: Data e Hora */}
          {currentStep === 2 && selectedService && (
            <div className="step-datetime">
              <div className="left">
                <h4>Data do Agendamento</h4>
                <input
                  type="date"
                  value={selectedDate || ""}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  onFocus={(e) => e.target.showPicker && e.target.showPicker()}
                  min={todayISO}
                />

                {loadingHorarios ? (
                  <div>Carregando horários...</div>
                ) : errorHorarios ? (
                  <div className="text-danger">Erro ao carregar horários: {errorHorarios}</div>
                ) : availableTimes.length > 0 ? (
                  <>
                    <h4>Horários Disponíveis</h4>
                    <div className="time-slots">
                      {availableTimes.map((t) => (
                        <div
                          key={t.id}
                          className={`time-slot ${selectedTime === t.hora ? "selected" : ""}`}
                          onClick={() => setSelectedTime(t.hora)}
                        >
                          {t.hora}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  selectedDate && <p>Nenhum horário disponível para esta data.</p>
                )}
              </div>

              <div className="right">
                <h3 className="left">Resumo do Agendamento</h3>
                <div className="reservation-summary">
                  <p><strong>Serviço:</strong> {selectedService?.nome || "—"}</p>
                  <p><strong>Profissional:</strong> {selectedFuncionario?.nome || "—"}</p>
                  <p><strong>Data:</strong> {formatDateToDisplay(selectedDate) || "—"}</p>
                  <p><strong>Horário:</strong> {selectedTime || "—"}</p>
                </div>
                {selectedTime && (
                  <Button variant="success" onClick={() => setCurrentStep(3)} style={{ marginTop: 12 }}>
                    Próximo
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Passo 3: Dados do cliente */}
          {currentStep === 3 && (
            <div className="step-client-data two-columns">
              <h1 className="titulo">Seus Dados</h1>
              <div className="columns-wrapper">
                <div className="left">
                  <div className="client-form">
                    <div className="form-group">
                      <h5 className="left">WhatsApp com DDD</h5>
                      <input
                        type="tel"
                        placeholder="(99)99999-9999"
                        value={phone}
                        onChange={handlePhoneChange}
                      />
                      {isRegistered && phone && (
                        <small style={{ color: "green", marginTop: 5, display: "block" }}>
                          Telefone identificado no cadastro
                        </small>
                      )}
                    </div>

                    {!isRegistered && (
                      <div className="form-group">
                        <h5 className="left">Nome Completo</h5>
                        <input
                          type="text"
                          placeholder="Seu nome"
                          value={name}
                          onChange={(e) => setName(e.target.value.replace(/[^A-Za-zÀ-ÿ\s]/g, ""))}
                        />
                        {name && name.length < 3 && (
                          <small style={{ color: "red", display: "block" }}>
                            Nome deve ter pelo menos 3 letras.
                          </small>
                        )}
                      </div>
                    )}

                    <div className="form-group">
                      <h5 className="left">Observações (opcional)</h5>
                      <textarea
                        placeholder="Observações caso exista alguma."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="right">
                  <h5 className="left">Resumo do Agendamento</h5>
                  <div className="reservation-summary">
                    <p><strong>Serviço:</strong> {selectedService?.nome || "—"}</p>
                    <p><strong>Profissional:</strong> {selectedFuncionario?.nome || "—"}</p>
                    <p><strong>Data:</strong> {formatDateToDisplay(selectedDate) || "—"}</p>
                    <p><strong>Horário:</strong> {selectedTime || "—"}</p>
                    <p><strong>WhatsApp:</strong> {phone || "—"}</p>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <Button
                      variant="success"
                      disabled={
                        !selectedService || !selectedFuncionario ||
                        !selectedDate || !selectedTime ||
                        !phone || (!isRegistered && !name)
                      }
                      onClick={openConfirm}
                    >
                      Confirmar Agendamento
                    </Button>
                  </div>
                </div>
              </div>

              <ConfirmacaoAgendamento
                show={showConfirm}
                onClose={closeConfirm}
                onConfirm={handleConfirmAgendamento}
                resumo={{
                  servico: selectedService?.nome,
                  funcionario: selectedFuncionario?.nome,
                  data: formatDateToDisplay(selectedDate),
                  hora: selectedTime,
                  telefone: phone,
                  observacao: notes,
                }}
              />
            </div>
          )}

          <div className="button-volta mt-4">
            {currentStep === 0 ? (
              <Button variant="secondary" onClick={() => navigate(`/${slug}`)}>Voltar</Button>
            ) : currentStep !== 1 ? (
              <Button variant="secondary" onClick={handleBack}>Voltar</Button>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default Agendamentos;
